import React, { useState, useEffect } from 'react';
import {
  Activity, Plus, X, Trash2, Calendar, Pill, ArrowUp, ArrowDown,
  Heart, FileText, Save,
} from 'lucide-react';
import {
  Badge, btnPrimary, btnSm, inp, neuShadowSm, cardWrap,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   TreatmentPlanModal — create or edit a treatment plan with itemized
   protocols (stem cell type / NK protocol / dosage / route / frequency /
   scheduled date) and longitudinal outcomes.
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: '#94A3B8' },
  { value: 'active', label: 'Active', color: '#15803D' },
  { value: 'paused', label: 'Paused', color: '#B45309' },
  { value: 'completed', label: 'Completed', color: '#2E8B98' },
  { value: 'cancelled', label: 'Cancelled', color: '#DC3545' },
];

const PROTOCOL_TYPES = [
  'stem_cell', 'nk_cell', 'exosome', 'peptide', 'supplement',
  'medication', 'iv_therapy', 'pbmt', 'hbot', 'other',
];

const ROUTES = ['IV', 'IM', 'SQ', 'oral', 'topical', 'inhalation', 'other'];

export default function TreatmentPlanModal({ planId, contactId, onClose, onSaved }) {
  const isCreate = !planId;
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);

  const [planName, setPlanName] = useState('');
  const [status, setStatus] = useState('draft');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [notes, setNotes] = useState('');
  const [protocols, setProtocols] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [showOutcomeForm, setShowOutcomeForm] = useState(false);

  useEffect(() => {
    if (!planId) return;
    (async () => {
      try {
        const r = await window.zignagenixAPI.getTreatmentPlan(planId);
        if (r && !r.error) {
          setPlanName(r.plan_name || '');
          setStatus(r.status || 'draft');
          setStartDate(r.start_date ? String(r.start_date).slice(0, 10) : '');
          setEndDate(r.end_date ? String(r.end_date).slice(0, 10) : '');
          setPrimaryGoal(r.primary_goal || '');
          setNotes(r.notes || '');
          setProtocols((r.protocols || []).map(p => ({
            protocolType: p.protocol_type,
            productName: p.product_name || '',
            dosageValue: p.dosage_value || '',
            dosageUnit: p.dosage_unit || '',
            route: p.route || '',
            frequency: p.frequency || '',
            scheduledDate: p.scheduled_date ? String(p.scheduled_date).slice(0, 16) : '',
            completedDate: p.completed_date ? String(p.completed_date).slice(0, 16) : '',
            notes: p.notes || '',
          })));
          setOutcomes(r.outcomes || []);
        }
      } finally { setLoading(false); }
    })();
  }, [planId]);

  function addProtocol() {
    setProtocols(p => [...p, {
      protocolType: 'stem_cell', productName: '', dosageValue: '', dosageUnit: 'mL',
      route: 'IV', frequency: '', scheduledDate: '', completedDate: '', notes: '',
    }]);
  }
  function removeProtocol(i) { setProtocols(p => p.filter((_, idx) => idx !== i)); }
  function moveProtocol(i, dir) {
    setProtocols(p => {
      const arr = [...p]; const swap = i + dir;
      if (swap < 0 || swap >= arr.length) return p;
      [arr[i], arr[swap]] = [arr[swap], arr[i]];
      return arr;
    });
  }
  function upProtocol(i, k, v) {
    setProtocols(p => p.map((pr, idx) => idx === i ? { ...pr, [k]: v } : pr));
  }

  async function save() {
    if (!planName.trim()) { alert('Plan name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        contactId, planName, status,
        startDate: startDate || null, endDate: endDate || null,
        primaryGoal: primaryGoal || null, notes: notes || null,
        protocols: protocols.map(p => ({
          protocolType: p.protocolType, productName: p.productName || null,
          dosageValue: p.dosageValue ? parseFloat(p.dosageValue) : null,
          dosageUnit: p.dosageUnit || null, route: p.route || null,
          frequency: p.frequency || null,
          scheduledDate: p.scheduledDate ? new Date(p.scheduledDate).toISOString() : null,
          completedDate: p.completedDate ? new Date(p.completedDate).toISOString() : null,
          notes: p.notes || null,
        })),
      };
      if (isCreate) {
        await window.zignagenixAPI.createTreatmentPlan(payload);
      } else {
        await window.zignagenixAPI.updateTreatmentPlan(planId, payload);
      }
      onSaved?.();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={{ ...modalStyle, width: 600 }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: 30, textAlign: 'center' }}>Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 760 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={16} color="#2E8B98" />
            <h2 style={modalTitle}>{isCreate ? 'New Treatment Plan' : `Edit Plan: ${planName}`}</h2>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>

        {/* Plan header */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><Lbl>Plan name *</Lbl>
            <input value={planName} onChange={e => setPlanName(e.target.value)} required style={inp}
              placeholder="e.g. Knee regeneration — 12-week protocol" /></div>
          <div><Lbl>Status</Lbl>
            <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><Lbl>Start date</Lbl><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} /></div>
          <div><Lbl>End date</Lbl><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} /></div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Lbl>Primary goal</Lbl>
          <input value={primaryGoal} onChange={e => setPrimaryGoal(e.target.value)} style={inp}
            placeholder="What outcome are we targeting? (e.g. reduce knee pain VAS from 7 to 2)" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <Lbl>Plan notes</Lbl>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inp, resize: 'vertical' }}
            placeholder="Background, contraindications, prior treatments, special considerations…" />
        </div>

        {/* Protocols */}
        <div style={{ ...cardWrap, padding: 12, marginBottom: 14, background: '#FAFBFC' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Pill size={14} color="#2E8B98" /> Protocols ({protocols.length})
            </span>
            <button type="button" onClick={addProtocol} style={btnSm}><Plus size={12} /> Add protocol</button>
          </div>
          {protocols.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', fontSize: 11, color: '#94A3B8' }}>
              No protocols yet. Add the items that make up this treatment plan.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {protocols.map((p, i) => (
                <div key={i} style={{ ...cardWrap, padding: 10, background: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Badge text={`#${i + 1}`} color="#2E8B98" />
                    <div style={{ flex: 1 }} />
                    <button type="button" onClick={() => moveProtocol(i, -1)} disabled={i === 0} style={iconBtn}>
                      <ArrowUp size={11} color={i === 0 ? '#CBD5E1' : '#475569'} />
                    </button>
                    <button type="button" onClick={() => moveProtocol(i, 1)} disabled={i === protocols.length - 1} style={iconBtn}>
                      <ArrowDown size={11} color={i === protocols.length - 1 ? '#CBD5E1' : '#475569'} />
                    </button>
                    <button type="button" onClick={() => removeProtocol(i)} style={iconBtn}>
                      <Trash2 size={11} color="#DC3545" />
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <div><Lbl>Type *</Lbl>
                      <select value={p.protocolType} onChange={e => upProtocol(i, 'protocolType', e.target.value)} style={inp}>
                        {PROTOCOL_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                      </select></div>
                    <div><Lbl>Product / source</Lbl>
                      <input value={p.productName} onChange={e => upProtocol(i, 'productName', e.target.value)} style={inp}
                        placeholder="e.g. Wharton's jelly MSCs" /></div>
                    <div><Lbl>Frequency</Lbl>
                      <input value={p.frequency} onChange={e => upProtocol(i, 'frequency', e.target.value)} style={inp}
                        placeholder="e.g. 3 doses over 5 days" /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <div><Lbl>Dosage value</Lbl>
                      <input type="number" step="any" value={p.dosageValue} onChange={e => upProtocol(i, 'dosageValue', e.target.value)} style={inp}
                        placeholder="e.g. 100" /></div>
                    <div><Lbl>Unit</Lbl>
                      <input value={p.dosageUnit} onChange={e => upProtocol(i, 'dosageUnit', e.target.value)} style={inp}
                        placeholder="mL / mg / 10⁶ cells" /></div>
                    <div><Lbl>Route</Lbl>
                      <select value={p.route} onChange={e => upProtocol(i, 'route', e.target.value)} style={inp}>
                        <option value="">—</option>
                        {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select></div>
                    <div><Lbl>Scheduled</Lbl>
                      <input type="datetime-local" value={p.scheduledDate} onChange={e => upProtocol(i, 'scheduledDate', e.target.value)} style={inp} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div><Lbl>Completed</Lbl>
                      <input type="datetime-local" value={p.completedDate} onChange={e => upProtocol(i, 'completedDate', e.target.value)} style={inp} /></div>
                    <div><Lbl>Notes</Lbl>
                      <input value={p.notes} onChange={e => upProtocol(i, 'notes', e.target.value)} style={inp}
                        placeholder="Site, batch, observations…" /></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Outcomes (only on edit; can't log against an unsaved plan) */}
        {!isCreate && (
          <div style={{ ...cardWrap, padding: 12, marginBottom: 14, background: '#FAFBFC' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Heart size={14} color="#DC2626" /> Outcomes ({outcomes.length})
              </span>
              <button type="button" onClick={() => setShowOutcomeForm(s => !s)} style={btnSm}>
                <Plus size={12} /> {showOutcomeForm ? 'Hide' : 'Log outcome'}
              </button>
            </div>
            {showOutcomeForm && (
              <OutcomeForm planId={planId} onLogged={() => {
                setShowOutcomeForm(false);
                window.zignagenixAPI.getTreatmentPlan(planId).then(r => setOutcomes(r.outcomes || []));
              }} />
            )}
            {outcomes.length === 0 ? (
              <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: '#94A3B8' }}>
                No outcomes logged yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {outcomes.map(o => (
                  <div key={o.id} style={{
                    padding: '8px 10px', background: '#fff', borderRadius: 8,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <div style={{
                      flexShrink: 0, width: 36, height: 36, borderRadius: 8,
                      background: outcomeColor(o.response_score),
                      color: '#fff', fontWeight: 800, fontSize: 14,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{o.response_score ?? '—'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: '#6C757D' }}>
                        {new Date(o.follow_up_date).toLocaleString()} · {o.recorded_by_name || 'system'}
                      </div>
                      {o.outcome_summary && (
                        <div style={{ fontSize: 12, color: '#212529', marginTop: 2 }}>{o.outcome_summary}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} style={btnPrimary}>
            <Save size={13} /> {saving ? 'Saving…' : (isCreate ? 'Create Plan' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Outcome form ─────────────────────────────────────────────────────────── */

function OutcomeForm({ planId, onLogged }) {
  const [followUpDate, setFollowUpDate] = useState(new Date().toISOString().slice(0, 16));
  const [responseScore, setResponseScore] = useState(7);
  const [outcomeSummary, setOutcomeSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await window.zignagenixAPI.logTreatmentOutcome({
        treatmentPlanId: planId,
        followUpDate: new Date(followUpDate).toISOString(),
        responseScore: parseInt(responseScore, 10),
        outcomeSummary: outcomeSummary || null,
      });
      setOutcomeSummary('');
      onLogged?.();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ ...cardWrap, padding: 10, background: '#fff', marginBottom: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
        <div><Lbl>Follow-up date</Lbl>
          <input type="datetime-local" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} style={inp} /></div>
        <div><Lbl>Response score (1-10)</Lbl>
          <input type="number" min="1" max="10" value={responseScore} onChange={e => setResponseScore(e.target.value)} style={inp} /></div>
      </div>
      <div style={{ marginBottom: 6 }}>
        <Lbl>Summary</Lbl>
        <textarea rows={2} value={outcomeSummary} onChange={e => setOutcomeSummary(e.target.value)}
          placeholder="What changed since last follow-up? Symptoms, vitals, patient self-report…"
          style={{ ...inp, resize: 'vertical' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={submit} disabled={submitting} style={btnPrimary}>
          {submitting ? 'Logging…' : 'Log outcome'}
        </button>
      </div>
    </div>
  );
}

function outcomeColor(score) {
  if (!score) return '#94A3B8';
  if (score >= 8) return '#15803D';
  if (score >= 5) return '#B45309';
  return '#DC2626';
}

const Lbl = ({ children }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>
    {children}
  </label>
);

const iconBtn = {
  background: '#F8F9FA', border: 'none', cursor: 'pointer',
  padding: 4, borderRadius: 6, boxShadow: neuShadowSm,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
